import { useEffect, useSyncExternalStore } from 'react';
import { clipVersionStore } from '@/stores/clipVersionStore';
import { activateVersioning, exitViewMode, restoreVersion, viewVersion } from '@/stores/versioning/controller';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';

/**
 * Reads version state and exposes the version actions.
 *
 * All the work lives in the versioning controller, which is a singleton — several components
 * render versioning UI and calling this hook from each of them costs one idempotent
 * activation, not one load and one subscription apiece.
 */
export default function useClipVersions() {
  const { clipId } = useClipsContext();

  // The banner mounts this hook for every clip, so the flag has to gate activation here.
  const isVersioningEnabled = !!useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot)[
    FeatureFlagKeys.Use_CL_Clip_Versioning
  ];

  const { versions, viewingVersion, isLoadingVersions, restoringVersion } = useSyncExternalStore(
    clipVersionStore.subscribe,
    clipVersionStore.getSnapshot
  );

  useEffect(() => {
    if (!clipId || !isVersioningEnabled) {
      return;
    }
    activateVersioning(clipId);
  }, [clipId, isVersioningEnabled]);

  return {
    versions,
    isLoadingVersions,
    restoringVersion,
    viewingVersion,
    handleView: viewVersion,
    handleRestore: restoreVersion,
    exitViewMode
  };
}
